import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../App";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { 
  ArrowLeft, User, Building, Mail, Save, Loader, Lock, Eye, EyeOff, Shield, CheckCircle,
  Smartphone, Key, AlertTriangle, Copy, RefreshCw
} from "lucide-react";
import SessionTimer from "@/components/SessionTimer";

const ProfilePage = () => {
  const { user } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Form states
  const [fullName, setFullName] = useState("");
  const [organization, setOrganization] = useState("");
  
  // Password change states
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);

  // MFA states
  const [mfaStatus, setMfaStatus] = useState({ mfa_enabled: false, backup_codes_remaining: 0 });
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaStep, setMfaStep] = useState("start"); // start, qr, verify, success
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showDisableMfa, setShowDisableMfa] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");

  useEffect(() => {
    fetchProfile();
    fetchMfaStatus();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get("/profile");
      setProfile(response.data);
      setFullName(response.data.full_name || "");
      setOrganization(response.data.organization || "");
    } catch (error) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const fetchMfaStatus = async () => {
    try {
      const response = await axios.get("/profile/mfa/status");
      setMfaStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch MFA status:", error);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.put("/profile", {
        full_name: fullName,
        organization: organization
      });
      toast.success("Profile updated successfully");
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    
    if (currentPassword === newPassword) {
      toast.error("New password must be different from current password");
      return;
    }
    
    setChangingPassword(true);
    
    try {
      await axios.put("/profile/password", {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      setPasswordChanged(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Password changed successfully!");
      
      setTimeout(() => {
        setShowPasswordForm(false);
        setPasswordChanged(false);
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const resetPasswordForm = () => {
    setShowPasswordForm(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordChanged(false);
  };

  // MFA Functions
  const startMfaSetup = async () => {
    setMfaLoading(true);
    try {
      const response = await axios.post("/profile/mfa/setup");
      setMfaSetupData(response.data);
      setMfaStep("qr");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start MFA setup");
    } finally {
      setMfaLoading(false);
    }
  };

  const verifyMfaSetup = async () => {
    if (mfaCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    
    setMfaLoading(true);
    try {
      await axios.post("/profile/mfa/verify", { code: mfaCode });
      setMfaStep("success");
      fetchMfaStatus();
      toast.success("MFA enabled successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code. Please try again.");
    } finally {
      setMfaLoading(false);
    }
  };

  const disableMfa = async () => {
    if (!disablePassword) {
      toast.error("Please enter your password");
      return;
    }
    
    setMfaLoading(true);
    try {
      await axios.delete("/profile/mfa", {
        data: { current_password: disablePassword, new_password: "dummy" }
      });
      setMfaStatus({ mfa_enabled: false, backup_codes_remaining: 0 });
      setShowDisableMfa(false);
      setDisablePassword("");
      toast.success("MFA disabled successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to disable MFA");
    } finally {
      setMfaLoading(false);
    }
  };

  const copyBackupCodes = () => {
    if (mfaSetupData?.backup_codes) {
      navigator.clipboard.writeText(mfaSetupData.backup_codes.join("\n"));
      toast.success("Backup codes copied to clipboard");
    }
  };

  const closeMfaSetup = () => {
    setShowMfaSetup(false);
    setMfaSetupData(null);
    setMfaCode("");
    setMfaStep("start");
  };

  // Password strength indicator
  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: "", color: "" };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    
    if (strength <= 2) return { strength, label: "Weak", color: "bg-red-500" };
    if (strength <= 3) return { strength, label: "Fair", color: "bg-amber-500" };
    if (strength <= 4) return { strength, label: "Good", color: "bg-blue-500" };
    return { strength, label: "Strong", color: "bg-green-500" };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-800 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-10 h-10 text-cyan-400 animate-spin mx-auto" />
          <p className="mt-3 text-blue-200/70 text-sm">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 via-blue-900 to-indigo-900">
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-md border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-3 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/dashboard" className="text-blue-200 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-lg font-heading font-semibold text-white">Profile Settings</h1>
          </div>
          <SessionTimer enabled={true} />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
        
        {/* Profile Information */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg">
          <div className="px-6 py-4 border-b border-white/10">
            <h2 className="text-sm font-semibold text-white flex items-center">
              <User className="w-4 h-4 mr-2" />
              Profile Information
            </h2>
          </div>
          <form onSubmit={handleUpdateProfile} className="p-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-blue-100 mb-1">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-300/50" />
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 bg-white/10 border border-white/20 rounded text-white focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm placeholder-blue-200/40"
                    placeholder="Your name"
                    data-testid="profile-fullname-input"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-blue-100 mb-1">
                  Organization
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-300/50" />
                  <input
                    type="text"
                    value={organization}
                    onChange={(e) => setOrganization(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 bg-white/10 border border-white/20 rounded text-white focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm placeholder-blue-200/40"
                    placeholder="Your organization"
                    data-testid="profile-organization-input"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-blue-100 mb-1">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-300/50" />
                <input
                  type="email"
                  value={profile?.email || ""}
                  disabled
                  className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded text-blue-200/60 text-sm"
                />
              </div>
              <p className="mt-1 text-xs text-blue-200/50">Email cannot be changed</p>
            </div>
            
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="flex items-center space-x-2 bg-cyan-500 text-white px-4 py-2 rounded text-sm font-medium hover:bg-cyan-400 disabled:opacity-50 transition"
                data-testid="save-profile-btn"
              >
                {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                <span>Save Changes</span>
              </button>
            </div>
          </form>
        </div>

        {/* MFA Section */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg">
          <div className="px-6 py-4 border-b border-white/10">
            <h2 className="text-sm font-semibold text-white flex items-center">
              <Smartphone className="w-4 h-4 mr-2" />
              Two-Factor Authentication (2FA)
            </h2>
          </div>
          
          <div className="p-6">
            {mfaStatus.mfa_enabled ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                      <Shield className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">2FA is enabled</p>
                      <p className="text-blue-200/60 text-xs">
                        {mfaStatus.backup_codes_remaining} backup codes remaining
                      </p>
                    </div>
                  </div>
                  <span className="bg-green-500/20 text-green-400 text-xs font-semibold px-3 py-1 rounded-full">
                    ACTIVE
                  </span>
                </div>
                
                {!showDisableMfa ? (
                  <button
                    onClick={() => setShowDisableMfa(true)}
                    className="text-red-400 hover:text-red-300 text-sm font-medium transition"
                  >
                    Disable 2FA
                  </button>
                ) : (
                  <div className="bg-red-500/10 border border-red-400/30 rounded-lg p-4 mt-4">
                    <div className="flex items-start space-x-3 mb-4">
                      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                      <div>
                        <p className="text-red-300 font-medium">Disable Two-Factor Authentication</p>
                        <p className="text-red-300/70 text-sm">This will make your account less secure. Enter your password to confirm.</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <input
                        type="password"
                        value={disablePassword}
                        onChange={(e) => setDisablePassword(e.target.value)}
                        placeholder="Enter your password"
                        className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-400 placeholder-blue-200/40"
                      />
                      <button
                        onClick={disableMfa}
                        disabled={mfaLoading}
                        className="bg-red-500 text-white px-4 py-2 rounded text-sm font-medium hover:bg-red-400 disabled:opacity-50 transition"
                      >
                        {mfaLoading ? "..." : "Disable"}
                      </button>
                      <button
                        onClick={() => { setShowDisableMfa(false); setDisablePassword(""); }}
                        className="text-blue-200/70 hover:text-white text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : !showMfaSetup ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                    <Shield className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">2FA is not enabled</p>
                    <p className="text-blue-200/60 text-xs">Add an extra layer of security to your account</p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowMfaSetup(true); setMfaStep("start"); }}
                  className="bg-cyan-500 text-white px-4 py-2 rounded text-sm font-medium hover:bg-cyan-400 transition"
                  data-testid="enable-mfa-btn"
                >
                  Enable 2FA
                </button>
              </div>
            ) : (
              <div>
                {/* MFA Setup Flow */}
                {mfaStep === "start" && (
                  <div className="text-center py-4">
                    <Smartphone className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">Set Up Two-Factor Authentication</h3>
                    <p className="text-blue-200/70 mb-6 max-w-md mx-auto">
                      You'll need an authenticator app like Google Authenticator, Authy, or 1Password to complete setup.
                    </p>
                    <div className="flex justify-center space-x-3">
                      <button
                        onClick={startMfaSetup}
                        disabled={mfaLoading}
                        className="bg-cyan-500 text-white px-6 py-2 rounded font-medium hover:bg-cyan-400 disabled:opacity-50 transition"
                      >
                        {mfaLoading ? <Loader className="w-5 h-5 animate-spin" /> : "Continue"}
                      </button>
                      <button
                        onClick={closeMfaSetup}
                        className="text-blue-200/70 hover:text-white px-4 py-2"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {mfaStep === "qr" && mfaSetupData && (
                  <div>
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* QR Code */}
                      <div className="text-center">
                        <h4 className="text-white font-medium mb-3">1. Scan QR Code</h4>
                        <div className="bg-white p-4 rounded-lg inline-block mb-3">
                          <img src={mfaSetupData.qr_code_url} alt="QR Code" className="w-48 h-48" />
                        </div>
                        <p className="text-blue-200/60 text-xs">
                          Can't scan? Manual key: <code className="bg-white/10 px-2 py-1 rounded text-cyan-300">{mfaSetupData.secret}</code>
                        </p>
                      </div>
                      
                      {/* Backup Codes */}
                      <div>
                        <h4 className="text-white font-medium mb-3">2. Save Backup Codes</h4>
                        <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-3">
                          <div className="grid grid-cols-2 gap-2">
                            {mfaSetupData.backup_codes.map((code, idx) => (
                              <code key={idx} className="text-cyan-300 text-sm font-mono">{code}</code>
                            ))}
                          </div>
                        </div>
                        <button
                          onClick={copyBackupCodes}
                          className="flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 text-sm"
                        >
                          <Copy className="w-4 h-4" />
                          <span>Copy codes</span>
                        </button>
                        <p className="text-amber-400/80 text-xs mt-2">
                          <AlertTriangle className="w-3 h-3 inline mr-1" />
                          Store these safely. They can't be shown again.
                        </p>
                      </div>
                    </div>
                    
                    {/* Verify Code */}
                    <div className="mt-6 pt-6 border-t border-white/10">
                      <h4 className="text-white font-medium mb-3">3. Enter Code from Authenticator</h4>
                      <div className="flex items-center space-x-3 max-w-sm">
                        <input
                          type="text"
                          value={mfaCode}
                          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          placeholder="000000"
                          maxLength={6}
                          className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded text-white text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-cyan-400 placeholder-blue-200/30"
                          data-testid="mfa-code-input"
                        />
                        <button
                          onClick={verifyMfaSetup}
                          disabled={mfaLoading || mfaCode.length !== 6}
                          className="bg-cyan-500 text-white px-6 py-3 rounded font-medium hover:bg-cyan-400 disabled:opacity-50 transition"
                          data-testid="verify-mfa-btn"
                        >
                          {mfaLoading ? <Loader className="w-5 h-5 animate-spin" /> : "Verify"}
                        </button>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <button onClick={closeMfaSetup} className="text-blue-200/70 hover:text-white text-sm">
                        Cancel setup
                      </button>
                    </div>
                  </div>
                )}

                {mfaStep === "success" && (
                  <div className="text-center py-6">
                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">2FA Enabled!</h3>
                    <p className="text-blue-200/70 mb-6">Your account is now protected with two-factor authentication.</p>
                    <button
                      onClick={closeMfaSetup}
                      className="bg-cyan-500 text-white px-6 py-2 rounded font-medium hover:bg-cyan-400 transition"
                    >
                      Done
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Password Change Section */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white flex items-center">
              <Lock className="w-4 h-4 mr-2" />
              Password
            </h2>
          </div>
          
          <div className="p-6">
            {!showPasswordForm ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white font-medium">Change your password</p>
                  <p className="text-xs text-blue-200/60 mt-1">Use a strong, unique password</p>
                </div>
                <button
                  onClick={() => setShowPasswordForm(true)}
                  className="flex items-center space-x-2 bg-white/10 text-white px-4 py-2 rounded text-sm font-medium hover:bg-white/20 transition"
                  data-testid="change-password-btn"
                >
                  <Key className="w-4 h-4" />
                  <span>Change Password</span>
                </button>
              </div>
            ) : passwordChanged ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 mx-auto mb-3 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
                <p className="text-sm font-medium text-green-400">Password changed successfully!</p>
              </div>
            ) : (
              <form onSubmit={handleChangePassword} className="space-y-4">
                {/* Current Password */}
                <div>
                  <label className="block text-sm font-medium text-blue-100 mb-1">
                    Current Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-300/50" />
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full pl-9 pr-10 py-2 bg-white/10 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 placeholder-blue-200/40"
                      placeholder="Enter current password"
                      required
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-300/50 hover:text-white"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                {/* New Password */}
                <div>
                  <label className="block text-sm font-medium text-blue-100 mb-1">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-300/50" />
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full pl-9 pr-10 py-2 bg-white/10 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 placeholder-blue-200/40"
                      placeholder="Enter new password (min 8 characters)"
                      required
                      minLength={8}
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-300/50 hover:text-white"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  
                  {/* Password Strength Indicator */}
                  {newPassword && (
                    <div className="mt-2">
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${passwordStrength.color} transition-all`}
                            style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                          />
                        </div>
                        <span className={`text-xs font-medium ${
                          passwordStrength.label === "Weak" ? "text-red-400" :
                          passwordStrength.label === "Fair" ? "text-amber-400" :
                          passwordStrength.label === "Good" ? "text-blue-400" :
                          "text-green-400"
                        }`}>
                          {passwordStrength.label}
                        </span>
                      </div>
                      <p className="text-xs text-blue-200/50 mt-1">
                        Use 8+ characters with uppercase, numbers & symbols
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Confirm New Password */}
                <div>
                  <label className="block text-sm font-medium text-blue-100 mb-1">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-300/50" />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`w-full pl-9 pr-3 py-2 bg-white/10 border rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 placeholder-blue-200/40 ${
                        confirmPassword && confirmPassword !== newPassword
                          ? "border-red-400/50"
                          : confirmPassword && confirmPassword === newPassword
                          ? "border-green-400/50"
                          : "border-white/20"
                      }`}
                      placeholder="Confirm new password"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                  {confirmPassword && confirmPassword !== newPassword && (
                    <p className="text-xs text-red-400 mt-1">Passwords don't match</p>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={resetPasswordForm}
                    className="px-4 py-2 text-sm font-medium text-blue-200/70 hover:text-white transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={changingPassword || !currentPassword || !newPassword || newPassword !== confirmPassword}
                    className="flex items-center space-x-2 bg-cyan-500 text-white px-4 py-2 rounded text-sm font-medium hover:bg-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    data-testid="submit-password-change-btn"
                  >
                    {changingPassword ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <Lock className="w-4 h-4" />
                    )}
                    <span>{changingPassword ? "Changing..." : "Update Password"}</span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Account Info */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg">
          <div className="px-6 py-4 border-b border-white/10">
            <h2 className="text-sm font-semibold text-white">Account Information</h2>
          </div>
          <div className="p-6">
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-blue-200/60">Role</p>
                <p className="font-medium text-white capitalize">{profile?.role}</p>
              </div>
              <div>
                <p className="text-blue-200/60">License</p>
                <p className="font-medium text-white capitalize">{profile?.license_type || "Trial"}</p>
              </div>
              <div>
                <p className="text-blue-200/60">Member Since</p>
                <p className="font-medium text-white">
                  {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : "-"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;
